import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import originalSchema from "../../gen/schema/witbooking.backoffice.languages.v1/Language.json";
import { SECTION } from "../../lib/constants";
import { FormConfiguration } from "../shared/crud/form";
import { TableColumns } from "../shared/crud/list";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { RendererProps } from "../shared/renderers";
import { StringRenderer } from "../shared/renderers/table";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { LanguageEntity } from "./domain/languages.entity";
import { getAllEntities, getEntity, updateEntity } from "./infrastructure/languages.api";
import { LanguageManagerMessages, tableHeaderMessages } from "./messages";

const messages = {
  ...defaultCrudMessages,
  ...LanguageManagerMessages,
  tableHeaders: tableHeaderMessages,
};

const schema = {
  ...originalSchema,
};

function CustomBooleanRenderer({ value: valueReq = "" }: RendererProps<string | boolean>) {
  return <>{valueReq ? "✔" : ""}</>;
}

function buildFormConfiguration(): FormConfiguration<LanguageEntity> {
  return {
    __fieldOrder: ["id", "name", "code", "primary", "active", "visible", "*"],
    __useProtoStyleArrays: true,
    id: {
      hidden: true,
    },
    name: {
      messages: {
        label: messages.tableHeaders.name,
      },
      readonly: true,
    },
    code: {
      messages: {
        label: messages.tableHeaders.code,
      },
      readonly: true,
    },
    primary: {
      widget: SwitchGroup,
      messages: {
        label: messages.tableHeaders.primary,
        description: messages.defaultDescription,
      },
    },
    active: {
      widget: SwitchGroup,
      messages: {
        label: messages.tableHeaders.active,
      },
    },
    visible: {
      widget: SwitchGroup,
      messages: {
        label: messages.tableHeaders.visible,
      },
    },
  };
}

function buildTableColumns(): TableColumns<LanguageEntity> {
  return {
    name: {
      renderer: StringRenderer,
      width: 150,
    },
    code: {
      renderer: StringRenderer,
      width: 150,
    },
    active: {
      renderer: CustomBooleanRenderer,
      width: 150,
    },
    visible: {
      renderer: CustomBooleanRenderer,
      width: 150,
    },
    primary: {
      renderer: CustomBooleanRenderer,
      width: 150,
    },
  };
}

function LanguageManager() {
  const intl = useIntl();
  const [visibleLanguages, setVisibleLanguages] = useState(0);

  const onGetAll = useCallback(async () => {
    const languages = await getAllEntities();
    setVisibleLanguages(languages.filter(l => l.visible).length);
    return languages;
  }, []);

  const onUpdate = useCallback(
    async (entity: LanguageEntity) => {
      if (visibleLanguages <= 1 && !entity.visible) {
        throw new Error(`- ${intl.formatMessage(messages.errorLastVisibleLanguage)}`);
      }
      return await updateEntity(entity);
    },
    [intl, visibleLanguages]
  );

  const formConfiguration = useMemo(() => buildFormConfiguration(), []);
  const tableColumns = useMemo(() => buildTableColumns(), []);

  return (
    <SimpleCrud
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      messages={messages}
      onGetAll={onGetAll}
      onGetOne={getEntity}
      onUpdate={onUpdate}
      hideExportButton
      section={SECTION.LANGUAGE_SETTINGS}
    />
  );
}

export default withAuthorizationVerifier(LanguageManager);
